/**
 * Accounting budget outlook: contracted dancer packages, last-year extras
 * (haircut 25%), and remaining unknown cancels from last year’s new vs
 * returning rates. All money is integer cents.
 */

export const DEPOSIT_CENTS = 45000
export const EXTRAS_KEEP_RATE = 0.75
export const AFTER_DEADLINE_LOSS_RATE = 0.5
/** Default “if we sell N” headcount for the full-house scenario. */
export const DEFAULT_FULL_HOUSE_HEADCOUNT = 65
export const ACCESS_PACKAGE_CENTS = 220000
export const DORM_PACKAGE_CENTS = 365000
export const COTTAGE_PACKAGE_CENTS = 410000
/** Inclusive: Apr 15 of the festival year (America/New_York). */
export const CANCEL_DEADLINE_MD = '04-15'
/** Inclusive: Jul 1 of the festival year — no remaining cancel forecast. */
export const FORECAST_END_MD = '07-01'

export function dollarsToCents (n) {
  return Math.round((Number(n) || 0) * 100)
}

export function dancerPackageCents (dancer) {
  let due = 0
  let paid = 0
  const deposit = Number(dancer && dancer.initialPayment) || 0
  due += deposit
  paid += deposit
  for (let i = 1; i <= 6; i++) {
    const amount = Number(dancer && dancer['payment' + i + 'Due']) || 0
    const status = dancer && dancer['payment' + i + 'Status']
    if (status && status !== 'N/A') {
      due += amount
      if (status === 'Paid') {
        paid += amount
      }
    }
  }
  return {
    dueCents: dollarsToCents(due),
    paidCents: dollarsToCents(paid)
  }
}

export function sumDancerPackages (dancers) {
  let dueCents = 0
  let paidCents = 0
  for (const d of dancers || []) {
    const p = dancerPackageCents(d)
    dueCents += p.dueCents
    paidCents += p.paidCents
  }
  return {
    dueCents,
    paidCents,
    outstandingCents: dueCents - paidCents
  }
}

function addonIsCommitted (r) {
  const s = r && r.status
  return s === 'paid' || s === 'pending' || s === 'invoiced' || s === 'feedback-posted'
}

function guestIsCommitted (g) {
  const s = g && g.paymentStatus
  return s === 'paid' || s === 'unpaid' || s === 'added_to_next'
}

function guestIsInPackages (g) {
  if (!g) {
    return false
  }
  return g.paymentStatus === 'added_to_next' || !!g.addedToPaymentNum
}

function costumeIsCommitted (d) {
  if (!d) {
    return false
  }
  return !!(d.costumeOrderPaid || d.costumeOrderCompleted || d.costumePaymentMethod === 'addToNext')
}

function costumeIsInPackages (d) {
  return !!(d && d.costumePaymentMethod === 'addToNext')
}

export function extrasFromRecords (records) {
  const src = records || {}
  let addonsPaid = 0
  let addonsCommitted = 0
  let merchPaid = 0
  let merchCommitted = 0
  let guestsPaid = 0
  let guestsCommitted = 0
  let guestsInPackages = 0
  let costumesPaid = 0
  let costumesCommitted = 0
  let costumesInPackages = 0
  for (const r of src.addonServices || []) {
    const cents = Math.round(Number(r && r.netCents) || 0)
    if (r && r.status === 'paid') {
      addonsPaid += cents
    }
    if (addonIsCommitted(r)) {
      addonsCommitted += cents
    }
  }
  for (const o of src.storeOrders || []) {
    const cents = Math.round(Number(o && o.totalCents) || 0)
    if (o && o.status === 'paid') {
      merchPaid += cents
      merchCommitted += cents
    }
  }
  for (const g of src.guests || []) {
    const cents = dollarsToCents(g && g.totalCost)
    if (g && g.paymentStatus === 'paid') {
      guestsPaid += cents
    }
    if (guestIsCommitted(g)) {
      guestsCommitted += cents
    }
    if (guestIsInPackages(g)) {
      guestsInPackages += cents
    }
  }
  for (const d of src.dancers || []) {
    const cents = dollarsToCents(d && d.costumeTotalCost)
    if (d && d.costumeOrderPaid) {
      costumesPaid += cents
    }
    if (costumeIsCommitted(d)) {
      costumesCommitted += cents
    }
    if (costumeIsInPackages(d)) {
      costumesInPackages += cents
    }
  }
  const paidCents = addonsPaid + merchPaid + guestsPaid + costumesPaid
  const committedCents = addonsCommitted + merchCommitted + guestsCommitted + costumesCommitted
  const inPackagesCents = guestsInPackages + costumesInPackages
  return {
    addonsCents: addonsPaid,
    merchCents: merchPaid,
    guestsCents: guestsPaid,
    costumesCents: costumesPaid,
    totalCents: paidCents,
    paidCents,
    committedCents,
    inPackagesCents,
    outsidePackagesCents: Math.max(0, committedCents - inPackagesCents)
  }
}

export function normalizeOutlookEmail (email) {
  return String(email || '').trim().toLowerCase()
}

function recordEmail (rec) {
  if (!rec) {
    return ''
  }
  return normalizeOutlookEmail(rec.email || (rec.snapshot && rec.snapshot.email))
}

function recordPersonId (rec) {
  if (!rec) {
    return ''
  }
  return String(rec.personId || (rec.snapshot && rec.snapshot.personId) || '')
}

function yearsFrom (raw) {
  const list = Array.isArray(raw) ? raw : []
  const out = []
  for (const v of list) {
    const n = Number(v)
    if (Number.isFinite(n) && n > 0) {
      out.push(n)
    }
  }
  return out
}

export function indexPeople (people) {
  const byId = new Map()
  const byEmail = new Map()
  for (const p of people || []) {
    if (!p || !p.id) {
      continue
    }
    byId.set(p.id, p)
    const email = normalizeOutlookEmail(p.email)
    if (email) {
      byEmail.set(email, p)
    }
  }
  return { byId, byEmail }
}

export function isReturningForYear (record, year, peopleIndex, priorEmails) {
  const y = Number(year)
  const idx = peopleIndex || { byId: new Map(), byEmail: new Map() }
  const personId = recordPersonId(record)
  const email = recordEmail(record)
  const person = (personId && idx.byId.get(personId)) ||
    (email && idx.byEmail.get(email)) ||
    null
  const years = []
  if (person) {
    years.push.apply(years, yearsFrom(person.yearsAttended))
  }
  if (record && Array.isArray(record.yearsAttended)) {
    years.push.apply(years, yearsFrom(record.yearsAttended))
  }
  if (record && record.snapshot && Array.isArray(record.snapshot.yearsAttended)) {
    years.push.apply(years, yearsFrom(record.snapshot.yearsAttended))
  }
  for (const n of years) {
    if (n < y) {
      return true
    }
  }
  if (email && priorEmails && priorEmails.has(email)) {
    return true
  }
  return false
}

export function emailsOf (records) {
  const set = new Set()
  for (const rec of records || []) {
    const email = recordEmail(rec)
    if (email) {
      set.add(email)
    }
  }
  return set
}

function splitCohort (records, year, peopleIndex, priorEmails) {
  const neu = []
  const returning = []
  for (const rec of records || []) {
    if (isReturningForYear(rec, year, peopleIndex, priorEmails)) {
      returning.push(rec)
    } else {
      neu.push(rec)
    }
  }
  return { new: neu, returning }
}

export function calendarDateInZone (now, timeZone) {
  const d = now instanceof Date ? now : new Date(now || Date.now())
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: timeZone || 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
  return fmt.format(d)
}

/**
 * before-deadline: full package (full refund until Apr 15)
 * after-deadline: 50% of (package − deposit)
 * ended: no remaining forecast (Jul 1, or year closed)
 */
export function cancelForecastPhase (now, festivalYear, yearEnded) {
  if (yearEnded) {
    return 'ended'
  }
  const y = Number(festivalYear)
  if (!Number.isFinite(y) || y < 2000) {
    return 'before-deadline'
  }
  const today = calendarDateInZone(now || new Date(), 'America/New_York')
  if (today >= y + '-' + FORECAST_END_MD) {
    return 'ended'
  }
  if (today >= y + '-' + CANCEL_DEADLINE_MD) {
    return 'after-deadline'
  }
  return 'before-deadline'
}

export function lossPerCancelCents (avgPackageCents, phase, depositCents) {
  const pkg = Math.max(0, Math.round(Number(avgPackageCents) || 0))
  const deposit = Math.round(Number(depositCents) || DEPOSIT_CENTS)
  if (phase === 'ended') {
    return 0
  }
  if (phase === 'after-deadline') {
    return Math.round(AFTER_DEADLINE_LOSS_RATE * Math.max(0, pkg - deposit))
  }
  return pkg
}

function averageDueCents (dancers) {
  const list = dancers || []
  if (!list.length) {
    return 0
  }
  return Math.round(sumDancerPackages(list).dueCents / list.length)
}

function cancelRate (canceledCount, stayedCount) {
  const started = canceledCount + stayedCount
  if (started <= 0) {
    return 0
  }
  return canceledCount / started
}

/**
 * Remaining unknown cancels from last year’s new vs returning rates.
 * Already-canceled people are out of contracted packages, so they are not
 * in the remaining loss — only cancels we have not seen yet.
 */
export function forecastCancelLoss (input) {
  const src = input || {}
  const year = Number(src.festivalYear)
  const lastYear = year - 1
  const peopleIndex = indexPeople(src.people)
  const lastPriorEmails = emailsOf(src.yearBeforeLast || [])
  const thisPriorEmails = emailsOf([].concat(src.lastYearDancers || [], src.lastYearCanceled || []))

  const lastActive = splitCohort(src.lastYearDancers, lastYear, peopleIndex, lastPriorEmails)
  const lastCanceled = splitCohort(src.lastYearCanceled, lastYear, peopleIndex, lastPriorEmails)
  const thisActive = splitCohort(src.thisYearDancers, year, peopleIndex, thisPriorEmails)
  const thisCanceled = splitCohort(src.thisYearCanceled, year, peopleIndex, thisPriorEmails)

  const lastNewStarted = lastActive.new.length + lastCanceled.new.length
  const lastRetStarted = lastActive.returning.length + lastCanceled.returning.length
  const rateNew = cancelRate(lastCanceled.new.length, lastActive.new.length)
  const rateRet = cancelRate(lastCanceled.returning.length, lastActive.returning.length)

  const thisNewStarted = thisActive.new.length + thisCanceled.new.length
  const thisRetStarted = thisActive.returning.length + thisCanceled.returning.length
  const expectedNew = rateNew * thisNewStarted
  const expectedRet = rateRet * thisRetStarted
  const remainingNew = Math.max(0, expectedNew - thisCanceled.new.length)
  const remainingRet = Math.max(0, expectedRet - thisCanceled.returning.length)

  const overallAvg = averageDueCents(src.thisYearDancers)
  const avgNew = averageDueCents(thisActive.new) || overallAvg
  const avgRet = averageDueCents(thisActive.returning) || overallAvg
  const phase = cancelForecastPhase(src.now, year, src.yearEnded)
  const lossNew = lossPerCancelCents(avgNew, phase)
  const lossRet = lossPerCancelCents(avgRet, phase)
  const remainingLossCents = Math.round(remainingNew * lossNew + remainingRet * lossRet)

  return {
    phase,
    rateNew,
    rateRet,
    lastNewCanceled: lastCanceled.new.length,
    lastNewStarted,
    lastRetCanceled: lastCanceled.returning.length,
    lastRetStarted,
    thisNewCanceled: thisCanceled.new.length,
    thisNewStarted,
    thisRetCanceled: thisCanceled.returning.length,
    thisRetStarted,
    remainingNew,
    remainingRet,
    remainingPeople: remainingNew + remainingRet,
    overallAvgCents: overallAvg,
    avgNewCents: avgNew,
    avgRetCents: avgRet,
    lossNewCents: lossNew,
    lossRetCents: lossRet,
    remainingLossCents
  }
}

/**
 * Recalculate remaining unknown-cancel $ if `headcount` people are
 * contracted, keeping this year’s already-canceled count and last year’s
 * new/returning rates. Used when playing with “if we sell N”.
 *
 * People already sold keep this year’s mix. Extra seats (N − sold) are
 * all new — alumni who are coming tend to be in already, and new dancers
 * cancel at the higher rate, so this is the worst case for the budget.
 */
export function scaleCancelLoss (cancels, headcount) {
  const src = cancels || {}
  const n = Math.max(0, Math.round(Number(headcount) || 0))
  const thisNewCanceled = Math.max(0, Math.round(Number(src.thisNewCanceled) || 0))
  const thisRetCanceled = Math.max(0, Math.round(Number(src.thisRetCanceled) || 0))
  const thisNewStarted = Math.max(0, Math.round(Number(src.thisNewStarted) || 0))
  const thisRetStarted = Math.max(0, Math.round(Number(src.thisRetStarted) || 0))
  const thisNewActive = Math.max(0, thisNewStarted - thisNewCanceled)
  const thisRetActive = Math.max(0, thisRetStarted - thisRetCanceled)
  const sold = thisNewActive + thisRetActive
  const rateNew = Number(src.rateNew) || 0
  const rateRet = Number(src.rateRet) || 0
  const lossNew = Math.round(Number(src.lossNewCents) || 0)
  const lossRet = Math.round(Number(src.lossRetCents) || 0)
  if (n <= 0) {
    return {
      remainingLossCents: 0,
      remainingNew: 0,
      remainingRet: 0,
      remainingPeople: 0
    }
  }
  let newActive
  let retActive
  if (n <= sold && sold > 0) {
    newActive = n * (thisNewActive / sold)
    retActive = n * (thisRetActive / sold)
  } else {
    const extra = Math.max(0, n - sold)
    newActive = thisNewActive + extra
    retActive = thisRetActive
  }
  const remainingNew = Math.max(0, rateNew * (newActive + thisNewCanceled) - thisNewCanceled)
  const remainingRet = Math.max(0, rateRet * (retActive + thisRetCanceled) - thisRetCanceled)
  return {
    remainingLossCents: Math.round(remainingNew * lossNew + remainingRet * lossRet),
    remainingNew,
    remainingRet,
    remainingPeople: remainingNew + remainingRet
  }
}

function parseRoomCap (raw) {
  if (raw === null || raw === undefined || raw === '') {
    return null
  }
  const n = Math.round(Number(raw))
  if (!Number.isFinite(n) || n < 0) {
    return null
  }
  return n
}

function takeUpTo (left, cap) {
  if (cap === null || cap <= 0 || left <= 0) {
    return 0
  }
  return Math.min(left, cap)
}

/**
 * Package revenue if we sell `headcount` tickets.
 * Fill cottage / standard dorm / large dorm caps at list price, then the
 * remainder at Festival Access. Unset caps count as zero of that type.
 */
export function fullHouseRevenue (input) {
  const src = input || {}
  const headcount = Math.max(0, Math.round(Number(src.headcount) || 0))
  const dancers = src.dancers || []
  const sold = dancers.length
  const accessCents = Math.round(Number(src.accessCents) || ACCESS_PACKAGE_CENTS)
  const dormCents = Math.round(Number(src.dormCents) || DORM_PACKAGE_CENTS)
  const cottageCents = Math.round(Number(src.cottageCents) || COTTAGE_PACKAGE_CENTS)
  const cottageLimit = parseRoomCap(src.cottageLimit)
  const standardLimit = parseRoomCap(src.standardDormLimit)
  const adaLimit = parseRoomCap(src.adaDormLimit)
  const combinedDormLimit = parseRoomCap(src.dormLimit)

  let left = headcount
  const cottages = takeUpTo(left, cottageLimit)
  left -= cottages
  let standardDorms = takeUpTo(left, standardLimit)
  left -= standardDorms
  let adaDorms = takeUpTo(left, adaLimit)
  left -= adaDorms
  if (standardLimit === null && adaLimit === null && combinedDormLimit !== null) {
    standardDorms = takeUpTo(left, combinedDormLimit)
    left -= standardDorms
  }
  const access = left
  return {
    headcount,
    sold,
    remaining: Math.max(0, headcount - sold),
    cottages,
    standardDorms,
    adaDorms,
    access,
    cottageLimit,
    standardLimit,
    adaLimit,
    packageCents: cottages * cottageCents +
      (standardDorms + adaDorms) * dormCents +
      access * accessCents,
    accessListCents: accessCents,
    dormListCents: dormCents,
    cottageListCents: cottageCents
  }
}

export function extrasOutlook (lastYearTotalCents, thisYear) {
  const lastYearCents = Math.round(Number(lastYearTotalCents) || 0)
  const estimateCents = Math.round(lastYearCents * EXTRAS_KEEP_RATE)
  let paidCents = 0
  let committedCents = 0
  let inPackagesCents = 0
  if (thisYear && typeof thisYear === 'object') {
    paidCents = Math.round(Number(thisYear.paidCents) || Number(thisYear.thisYearCents) || 0)
    committedCents = Math.round(Number(thisYear.committedCents) || paidCents)
    inPackagesCents = Math.round(Number(thisYear.inPackagesCents) || 0)
  } else {
    paidCents = Math.round(Number(thisYear) || 0)
    committedCents = paidCents
  }
  const totalOutlookCents = Math.max(estimateCents, committedCents)
  return {
    lastYearCents,
    estimateCents,
    thisYearCents: paidCents,
    committedCents,
    inPackagesCents,
    outlookCents: Math.max(0, totalOutlookCents - inPackagesCents)
  }
}

/**
 * Empty input clears this year’s typed plan (inherit last year).
 * “0” is an explicit $0 plan.
 */
export function parseForecastInput (raw) {
  const trimmed = String(raw == null ? '' : raw).trim()
  if (trimmed === '') {
    return { unset: true, cents: 0 }
  }
  const dollars = Number(trimmed)
  if (!Number.isFinite(dollars)) {
    return { unset: true, cents: 0 }
  }
  return { unset: false, cents: Math.round(dollars * 100) }
}

function resolveLineForecast (src, spec) {
  const input = src || {}
  const byYear = spec.byYear && typeof spec.byYear === 'object' ? spec.byYear : {}
  const key = String(input.year)
  if (Object.prototype.hasOwnProperty.call(byYear, key)) {
    return { cents: Math.round(Number(byYear[key]) || 0), source: 'typed' }
  }
  const legacy = Math.round(Number(spec.legacyCents) || 0)
  if (legacy > 0) {
    return { cents: legacy, source: 'typed' }
  }
  const prior = Math.round(Number(spec.priorCents) || 0)
  if (!input.yearEnded && prior > 0) {
    return { cents: prior, source: 'prior-actual' }
  }
  return { cents: 0, source: 'none' }
}

/**
 * Open-year expense plan for a product line.
 * Year-specific typed value wins, then a legacy global forecast, then last
 * year's actual spend (gross — revenue is carried on the revenue side).
 */
export function resolveLineForecastExpense (input) {
  const src = input || {}
  return resolveLineForecast(src, {
    byYear: src.forecastExpenseByYear,
    legacyCents: Number(src.forecastExpenseCents) || Number(src.budgetCents) || 0,
    priorCents: Number(src.priorYearExpenseCents) || Number(src.priorYearActualCents) || 0
  })
}

/**
 * Open-year revenue plan for a product line.
 * Same precedence as expense: typed year map, then legacy typed, then last
 * year's actual revenue (Costume ~$6k on Fc revenue, ~$11k on Fc expense).
 */
export function resolveLineForecastRevenue (input) {
  const src = input || {}
  return resolveLineForecast(src, {
    byYear: src.forecastRevenueByYear,
    legacyCents: Number(src.forecastRevenueCents) || 0,
    priorCents: Number(src.priorYearRevenueCents) || 0
  })
}

export function predictedRevenueCents (parts) {
  const dancerDueCents = Math.round((parts && parts.dancerDueCents) || 0)
  const remainingCancelLossCents = Math.round((parts && parts.remainingCancelLossCents) || 0)
  const extrasOutlookCents = Math.round((parts && parts.extrasOutlookCents) || 0)
  const lineForecastRevenueCents = Math.round((parts && parts.lineForecastRevenueCents) || 0)
  const outlook = Math.max(0, dancerDueCents - remainingCancelLossCents + extrasOutlookCents + lineForecastRevenueCents)
  const actualRevenueCents = Math.round((parts && parts.actualRevenueCents) || 0)
  return Math.max(outlook, actualRevenueCents)
}

export function buildBudgetOutlook (input) {
  const src = input || {}
  const thisDancers = src.dancers || (src.thisYear && src.thisYear.dancers) || []
  const packages = sumDancerPackages(thisDancers)
  const lastYearExtras = extrasFromRecords(src.lastYear || {})
  const thisYearExtras = extrasFromRecords(src.thisYear || { dancers: thisDancers })
  const extras = extrasOutlook(lastYearExtras.totalCents, {
    paidCents: thisYearExtras.paidCents || thisYearExtras.totalCents,
    committedCents: thisYearExtras.committedCents,
    inPackagesCents: thisYearExtras.inPackagesCents
  })
  const cancels = forecastCancelLoss({
    thisYearDancers: thisDancers,
    thisYearCanceled: src.thisYearCanceled || [],
    lastYearDancers: src.lastYearDancers || (src.lastYear && src.lastYear.dancers) || [],
    lastYearCanceled: src.lastYearCanceled || [],
    yearBeforeLast: src.yearBeforeLast || [],
    people: src.people || [],
    festivalYear: src.festivalYear,
    now: src.now,
    yearEnded: src.yearEnded
  })
  return {
    packages,
    lastYearExtras,
    thisYearExtras,
    extras,
    cancels,
    predictedRevenueCents: predictedRevenueCents({
      dancerDueCents: packages.dueCents,
      remainingCancelLossCents: cancels.remainingLossCents,
      extrasOutlookCents: extras.outlookCents,
      actualRevenueCents: src.actualRevenueCents
    })
  }
}
